import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ArrowLeft, Truck, User, Wrench } from "lucide-react";

const redirectFor = (role) => (role === "admin" ? "/admin" : role === "partner" ? "/partner/dashboard" : "/dashboard");

const inputCls =
  "w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-ksy-ink placeholder-slate-400 focus:border-ksy-blue focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors";
const labelCls = "block text-sm font-semibold text-slate-700 mb-1.5";

export default function Auth({ mode }) {
  const isRegister = mode === "register";
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "partner" ? "partner" : "client");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", city: "", state: "", cdl_experience: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (user) navigate(redirectFor(user.role), { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = isRegister
        ? await register({ ...form, role })
        : await login(form.email, form.password);
      navigate(redirectFor(data.role), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ksy-bg px-5 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-ksy-navy transition-colors mb-6" data-testid="auth-back">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <div className="flex items-center gap-2.5 mb-6">
            <span className="grid place-items-center w-10 h-10 rounded-lg bg-ksy-navy text-white"><Truck className="w-5 h-5" /></span>
            <div>
              <div className="font-display font-extrabold text-lg text-ksy-navy leading-tight">
                {isRegister ? "Create your account" : "Welcome back"}
              </div>
              <div className="text-xs text-slate-400">KS&amp;Y Transport LLC</div>
            </div>
          </div>

          {isRegister && (
            <div className="grid grid-cols-2 gap-2 mb-5" data-testid="role-toggle">
              <button type="button" onClick={() => setRole("client")} className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold border transition-colors ${role === "client" ? "bg-ksy-blue text-white border-ksy-blue" : "bg-white text-slate-600 border-slate-200"}`} data-testid="role-client">
                <User className="w-4 h-4" /> Client
              </button>
              <button type="button" onClick={() => setRole("partner")} className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold border transition-colors ${role === "partner" ? "bg-ksy-blue text-white border-ksy-blue" : "bg-white text-slate-600 border-slate-200"}`} data-testid="role-partner">
                <Wrench className="w-4 h-4" /> Partner / Driver
              </button>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {isRegister && (
              <div><label className={labelCls}>Full name</label><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="auth-name" /></div>
            )}
            <div><label className={labelCls}>Email</label><input type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="auth-email" /></div>
            <div><label className={labelCls}>Password</label><input type="password" required value={form.password} onChange={set("password")} className={inputCls} data-testid="auth-password" /></div>
            {isRegister && (
              <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="auth-phone" /></div>
            )}
            {isRegister && role === "partner" && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>City</label><input value={form.city} onChange={set("city")} className={inputCls} data-testid="auth-city" /></div>
                <div><label className={labelCls}>State</label><input value={form.state} onChange={set("state")} className={inputCls} data-testid="auth-state" /></div>
                <div className="col-span-2"><label className={labelCls}>CDL experience</label>
                  <select value={form.cdl_experience} onChange={set("cdl_experience")} className={inputCls} data-testid="auth-experience">
                    <option value="">—</option>
                    <option>Less than 1 year</option><option>1–3 years</option><option>3–5 years</option><option>5+ years</option>
                  </select>
                </div>
              </div>
            )}
            {error && <p className="text-red-600 text-sm" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-ksy-blue text-white font-bold px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60" data-testid="auth-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Please wait..." : isRegister ? "Create account" : "Sign in"}
            </button>
          </form>

          <p className="mt-5 text-sm text-slate-500 text-center">
            {isRegister ? (
              <>Already have an account? <Link to="/login" className="font-bold text-ksy-blue" data-testid="switch-login">Sign in</Link></>
            ) : (
              <>New here? <Link to="/register" className="font-bold text-ksy-blue" data-testid="switch-register">Create an account</Link></>
            )}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
